import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Scale, ServerCog, Users, Landmark, Calendar, MapPin, Megaphone } from "lucide-react";
import RichText from "@/components/RichText";

const PILLAR_ICONS = [Scale, ServerCog, Users, Landmark];

const DEFAULT_HERO_IMAGES = [
  "https://static.prod-images.emergentagent.com/jobs/69bc8a3c-ed6c-4b85-a9a2-ec966ad0ef18/images/22e1c01b0f2f82ef07be7ccc892b0efd2c3f9d29ea5fe94b47095f073f352d95.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/69bc8a3c-ed6c-4b85-a9a2-ec966ad0ef18/images/7c3710366a8aa1f38a17a4ebd1eca54660317c5ad425852d2e4d0750380c2a36.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/69bc8a3c-ed6c-4b85-a9a2-ec966ad0ef18/images/c96829e0de1001cf6f241ac7de24d28a8a20c4fa55fcbc9d0f1393a85c755ca5.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/69bc8a3c-ed6c-4b85-a9a2-ec966ad0ef18/images/12501d923922002e00bd25d6fe35093699461b3eca064c38aeea8d100dd961d3.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/69bc8a3c-ed6c-4b85-a9a2-ec966ad0ef18/images/a489ac78d70b280eee9f912f75afaf337a476638ea9b179a697aacaccb342326.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/69bc8a3c-ed6c-4b85-a9a2-ec966ad0ef18/images/4e1b85760efd0f91e8442fe0cbe9e966272c1244586d97a18d521d957a59ca17.jpeg",
];

export default function Home() {
  const { t } = useContent();
  const [trainings, setTrainings] = useState([]);
  const [news, setNews] = useState([]);
  const [heroGallery, setHeroGallery] = useState([]);
  const [heroSpeed, setHeroSpeed] = useState(30);

  useEffect(() => {
    api.get("/trainings").then((r) => setTrainings(r.data.slice(0, 3))).catch(() => {});
    api.get("/announcements").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/site-extras").then((r) => { setHeroGallery(r.data.hero_gallery || []); setHeroSpeed(r.data.hero_speed || 30); }).catch(() => {});
  }, []);

  const heroImg = t("home_hero_image") ? fileUrl(t("home_hero_image")) : "/hero-building.jpg";
  const gal = heroGallery.length ? heroGallery : DEFAULT_HERO_IMAGES;

  const stats = [1, 2, 3, 4].map((i) => ({
    value: t(`home_stat_${i}_value`),
    label: t(`home_stat_${i}_label`),
  }));

  const pillars = [1, 2, 3, 4].map((i) => ({
    title: t(`home_pillar_${i}_title`),
    desc: t(`home_pillar_${i}_desc`),
  }));

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-ztk-navydark pt-[68px] text-white">
        <div className="hero-grid absolute inset-0 opacity-50" />
        <div className="absolute -left-20 top-1/3 h-96 w-96 rounded-full bg-ztk-blue/20 blur-[120px]" />
        <div className="absolute right-0 top-0 h-80 w-80 rounded-full bg-ztk-red/10 blur-[120px]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-8 sm:px-6 lg:grid-cols-2 lg:px-8 lg:pb-28 lg:pt-10">
          <div className="animate-fade-up">
            <span className="label-mono inline-flex items-center gap-2 rounded-full border border-ztk-glow/30 bg-ztk-blue/10 px-4 py-1.5 text-ztk-glow" data-testid="home-badge">
              <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-ztk-yellow" />
              {t("home_badge")}
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {t("home_hero_title")}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">{t("home_hero_subtitle")}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-ztk-blue hover:bg-ztk-glow" data-testid="home-cta-register">
                <Link to="/rejestracja" className="gap-2">Dołącz do Izby <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10" data-testid="home-cta-trainings">
                <Link to="/szkolenia">Konferencje i szkolenia</Link>
              </Button>
            </div>
          </div>
          <div className="animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
              <img
                src={heroImg}
                alt="Związek Telewizji Kablowych"
                className="h-[380px] w-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* TRADITION + AUTO GALLERY */}
        <div className="relative border-t border-white/10 bg-black/20">
          <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:px-8">
            <div data-testid="home-tradition">
              <div className="font-display text-4xl font-extrabold text-ztk-glow lg:text-5xl">30 lat doświadczenia</div>
              <p className="mt-2 text-lg text-slate-300">Ważny głos polskiej branży telekomunikacyjnej</p>
            </div>
            {gal.length > 0 && (
              <div className="relative overflow-hidden" data-testid="hero-gallery">
                <style>{`@keyframes ztk-hgal{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>
                <div className="flex w-max gap-3" style={{ animation: `ztk-hgal ${heroSpeed || 30}s linear infinite` }}>
                  {[...gal, ...gal].map((img, i) => (
                    <img key={i} src={fileUrl(img)} alt="" className="h-28 w-[calc((100vw-3rem)/4)] max-w-[220px] shrink-0 rounded-lg object-cover" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="cable-accent h-[3px] w-full" />
      </section>

      {/* ANNOUNCEMENTS */}
      {news.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-4 pt-20 sm:px-6 lg:px-8 lg:pt-28">
          <div className="flex items-end justify-between">
            <div>
              <p className="label-mono flex items-center gap-2 text-ztk-blue"><Megaphone className="h-4 w-4" /> Aktualności</p>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-ztk-navy dark:text-white lg:text-4xl">
                Najnowsze ogłoszenia Izby
              </h2>
            </div>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {news.map((a, i) => (
              <Link key={a.id} to={`/aktualnosci/${a.id}`} data-testid={`home-news-${a.id}`}
                className="group animate-fade-up block overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ animationDelay: `${i * 0.08}s` }}>
                {a.image ? (
                  <div className="h-40 overflow-hidden"><img src={fileUrl(a.image)} alt={a.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" /></div>
                ) : (
                  <div className="cable-accent h-2 w-full" />
                )}
                <div className="p-6">
                  <p className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString("pl-PL")}</p>
                  <h3 className="mt-2 font-display text-lg font-semibold text-ztk-navy dark:text-white">{a.title}</h3>
                  <RichText html={a.body} className="mt-2 line-clamp-3 text-sm text-muted-foreground" />
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ztk-blue">Czytaj więcej <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* PILLARS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <p className="label-mono text-ztk-blue">Czym się zajmujemy</p>
        <h2 className="mt-2 max-w-2xl font-display text-3xl font-bold tracking-tight text-ztk-navy dark:text-white lg:text-4xl">
          Wsparcie dla operatorów na każdym poziomie
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <div
                key={i}
                data-testid={`home-pillar-${i + 1}`}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-7 transition-all hover:-translate-y-1 hover:border-ztk-blue/40 hover:shadow-xl"
              >
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-ztk-blue/10 text-ztk-blue transition-colors group-hover:bg-ztk-blue group-hover:text-white">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-semibold text-ztk-navy dark:text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TRAININGS PREVIEW */}
      <section className="border-y border-border bg-secondary/50">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <div>
              <p className="label-mono text-ztk-blue">Kalendarz</p>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-ztk-navy dark:text-white lg:text-4xl">
                Najbliższe wydarzenia
              </h2>
            </div>
            <Button asChild variant="ghost" className="hidden gap-1.5 text-ztk-blue sm:flex">
              <Link to="/szkolenia">Wszystkie <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {trainings.map((tr) => (
              <Link
                to={`/szkolenia/${tr.id}`}
                key={tr.id}
                data-testid={`home-event-${tr.id}`}
                className="group overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="h-40 overflow-hidden bg-ztk-navy">
                  <img
                    src={tr.images?.length ? fileUrl(tr.images[0]) : "https://images.unsplash.com/photo-1571645163064-77faa9676a46?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"}
                    alt={tr.title}
                    className="h-full w-full object-cover opacity-90 transition-transform group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{tr.event_date ? new Date(tr.event_date).toLocaleDateString("pl-PL") : "—"}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{tr.location}</span>
                  </div>
                  <h3 className="mt-3 font-display text-lg font-semibold text-ztk-navy dark:text-white">{tr.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{tr.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
